import { UserProfile, GameSession, UnlockedAchievement } from '../types/profile';
import { UserStateType } from '../hooks/useUserState';
import { OfflineProfileService } from './offlineProfileService';
import { SupabaseProfileService } from './supabaseProfileService';

export class UnifiedProfileService {
  
  /**
   * Get or create profile based on user state
   */
  static async getProfile(userState: UserStateType, userId?: string): Promise<UserProfile> {
    switch (userState) {
      case 'authenticated-online':
        return this.handleAuthenticatedOnline(userId!);
      
      case 'authenticated-offline':
        return this.handleAuthenticatedOffline(userId!);
      
      case 'guest-online':
      case 'guest-offline':
        return this.handleGuest();
    }
  }

  /**
   * Save game session based on user state
   */
  static async saveGameSession(
    gameSession: GameSession, 
    userState: UserStateType, 
    userId?: string
  ): Promise<UnlockedAchievement[]> {
    switch (userState) {
      case 'authenticated-online':
        return this.saveAuthenticatedOnline(gameSession, userId!);
      
      case 'authenticated-offline':
        return this.saveAuthenticatedOffline(gameSession, userId!);
      
      case 'guest-online':
      case 'guest-offline':
        return this.saveGuest(gameSession);
    }
  }

  /**
   * Update profile info based on user state
   */
  static async updateProfile(
    updates: { username?: string; avatar?: string },
    userState: UserStateType,
    userId?: string
  ): Promise<void> {
    // Always update locally first
    OfflineProfileService.updateOfflineProfile(updates);
    
    // If authenticated and online, also sync to cloud
    if (userState === 'authenticated-online' && userId) {
      try {
        await SupabaseProfileService.updateProfile(updates, userId);
      } catch (error) {
        console.warn('Failed to sync profile update to cloud:', error);
        // Local update already succeeded, so this is non-blocking
      }
    }
  }

  /**
   * Sync data when coming online (authenticated users only)
   */
  static async syncWhenOnline(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      return await OfflineProfileService.syncWithSupabase(userId);
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Clear all data (for sign out)
   */
  static clearAllData(): void {
    OfflineProfileService.clearOfflineData();
  }

  // Private helper methods for each state

  private static async handleAuthenticatedOnline(userId: string): Promise<UserProfile> {
    try {
      // First check if user exists in database (determines if they're new or existing)
      const cloudProfile = await SupabaseProfileService.getOrCreateProfile(userId);
      
      // Check if this is a truly new user (just created profile vs existing user signing in)
      const isNewUser = !cloudProfile.stats || cloudProfile.stats.totalGames === 0;
      
      if (isNewUser) {
        console.log('🆕 New authenticated user detected - creating fresh profile');
        // For new users, create a fresh offline profile to avoid guest data contamination
        const freshProfile = OfflineProfileService.resetForNewAuthenticatedUser(userId);
        
        // Return the fresh cloud profile (no merging needed for new users)
        return cloudProfile;
      } else {
        console.log('👤 Existing authenticated user - merging profiles');
        // For existing users, get offline profile and merge
        const offlineProfile = OfflineProfileService.getOrCreateOfflineProfile(userId);
        
        // Try to sync pending data first
        if (offlineProfile.pendingGameSessions.length > 0) {
          await OfflineProfileService.syncWithSupabase(userId);
        }
        
        // Merge profiles (offline takes precedence for conflicts)
        const mergedProfile = this.mergeProfiles(offlineProfile, cloudProfile);
        
        // Save merged result locally
        OfflineProfileService.saveOfflineProfile(mergedProfile as any);
        
        return mergedProfile;
      }
    } catch (error) {
      console.warn('Cloud sync failed, using offline profile:', error);
      // Fall back to offline profile, but make sure it's clean for new authenticated users
      const offlineProfile = OfflineProfileService.getOrCreateOfflineProfile(userId);
      return offlineProfile;
    }
  }

  private static handleAuthenticatedOffline(userId: string): Promise<UserProfile> {
    // Just return offline profile, but mark as needing sync
    const profile = OfflineProfileService.getOrCreateOfflineProfile(userId);
    return Promise.resolve(profile);
  }

  private static handleGuest(): Promise<UserProfile> {
    // Use offline profile with no user ID
    const profile = OfflineProfileService.getOrCreateOfflineProfile();
    return Promise.resolve(profile);
  }

  private static async saveAuthenticatedOnline(gameSession: GameSession, userId: string): Promise<UnlockedAchievement[]> {
    // Save offline first (always)
    const achievements = OfflineProfileService.addGameSessionToOfflineProfile(gameSession, userId);
    
    // Try to sync to cloud
    try {
      await SupabaseProfileService.updateProfileAfterGame(gameSession, userId);
      
      // Remove from pending since it was successfully synced
      const profile = OfflineProfileService.getOrCreateOfflineProfile(userId);
      profile.pendingGameSessions = profile.pendingGameSessions.filter(
        session => session.startedAt !== gameSession.startedAt
      );
      OfflineProfileService.saveOfflineProfile(profile);
    } catch (error) {
      console.warn('Failed to sync game session to cloud:', error);
      // Session is already saved offline, so no data loss
    }
    
    return achievements;
  }

  private static saveAuthenticatedOffline(gameSession: GameSession, userId: string): Promise<UnlockedAchievement[]> {
    // Just save offline, will sync when online
    const achievements = OfflineProfileService.addGameSessionToOfflineProfile(gameSession, userId);
    return Promise.resolve(achievements);
  }

  private static saveGuest(gameSession: GameSession): Promise<UnlockedAchievement[]> {
    // Save to offline profile with no user ID
    const achievements = OfflineProfileService.addGameSessionToOfflineProfile(gameSession);
    return Promise.resolve(achievements);
  }

  private static mergeProfiles(offline: any, cloud: UserProfile): any {
    // Merge achievements (offline takes precedence)
    const offlineAchievementIds = new Set(offline.achievements.map((a: any) => a.id));
    const cloudAchievements = cloud.achievements.filter(a => !offlineAchievementIds.has(a.id));
    const mergedAchievements = [...offline.achievements, ...cloudAchievements];

    return {
      ...cloud,
      achievements: mergedAchievements,
      // Keep offline structure for pending sessions
      pendingGameSessions: offline.pendingGameSessions || [],
      lastSyncAt: new Date().toISOString(),
      // Prefer offline preferences (user may have changed them offline)
      preferences: offline.preferences || cloud.preferences,
      // Keep offline profile marker
      isOfflineProfile: true
    };
  }
} 